/**
 * HH Finance - Transactions Table Component
 *
 * Displays invoices or bills in a table format
 */

import React from 'react';
import styled from 'styled-components';
import { Card } from '@shared/components/card';
import { Badge } from '@shared/components/badge';
import { formatCurrency, formatDate } from '../utils';
import type { InvoiceSummary, BillSummary, InvoiceStatus, BillStatus } from '../types';

type Transaction = InvoiceSummary | BillSummary;
type TransactionStatus = InvoiceStatus | BillStatus;

export interface TransactionsTableProps {
  title: string;
  icon?: string;
  type: 'invoices' | 'bills';
  transactions: Transaction[];
  limit?: number;
  onViewAll?: () => void;
  onRowClick?: (transaction: Transaction) => void;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};
  margin: 0;
`;

const Icon = styled.span`
  font-size: 20px;
`;

const ViewAllLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.primary || '#3b82f6'};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;

  &:hover {
    background-color: ${({ theme }) => theme.backgroundHover || '#f0f7ff'};
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 8px;
  border-bottom: 2px solid ${({ theme }) => theme.border || '#e5e5e5'};
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &.right {
    text-align: right;
  }
`;

const Tr = styled.tr<{ clickable?: boolean }>`
  cursor: ${({ clickable }) => clickable ? 'pointer' : 'default'};

  &:hover {
    background-color: ${({ theme, clickable }) =>
      clickable ? theme.backgroundHover || '#f9fafb' : 'transparent'};
  }
`;

const Td = styled.td`
  padding: 10px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.border || '#f0f0f0'};
  font-size: 13px;
  color: ${({ theme }) => theme.textPrimary || '#1a1a1a'};

  &.right {
    text-align: right;
  }
`;

const EntityName = styled.div`
  font-weight: 500;
`;

const DocNumber = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
`;

const Balance = styled.span`
  font-weight: 600;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  font-size: 14px;
`;

// Map status to badge variant
const statusVariantMap: Record<TransactionStatus, 'success' | 'warning' | 'error' | 'default'> = {
  paid: 'success',
  partial: 'warning',
  open: 'default',
  overdue: 'error',
};

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  title,
  icon,
  type,
  transactions,
  limit = 5,
  onViewAll,
  onRowClick,
}) => {
  const displayedTransactions = limit ? transactions.slice(0, limit) : transactions;
  const hasMore = transactions.length > displayedTransactions.length;
  const isInvoice = type === 'invoices';

  return (
    <Card>
      <Header>
        <TitleRow>
          {icon && <Icon>{icon}</Icon>}
          <Title>{title}</Title>
        </TitleRow>
        {onViewAll && hasMore && (
          <ViewAllLink onClick={onViewAll}>
            View all ({transactions.length})
          </ViewAllLink>
        )}
      </Header>

      {displayedTransactions.length === 0 ? (
        <EmptyState>
          No {type === 'invoices' ? 'open invoices' : 'open bills'}
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>{isInvoice ? 'Customer' : 'Vendor'}</Th>
              <Th>Due Date</Th>
              <Th className="right">Balance</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {displayedTransactions.map((txn) => {
              const invoice = txn as InvoiceSummary;
              const bill = txn as BillSummary;
              const name = isInvoice ? invoice.customerName : bill.vendorName;
              const docNum = txn.docNumber;

              return (
                <Tr
                  key={txn.id}
                  clickable={!!onRowClick}
                  onClick={() => onRowClick?.(txn)}
                >
                  <Td>
                    <EntityName>{name || 'Unknown'}</EntityName>
                    {docNum && <DocNumber>#{docNum}</DocNumber>}
                  </Td>
                  <Td>{formatDate(txn.dueDate)}</Td>
                  <Td className="right">
                    <Balance>{formatCurrency(txn.balance)}</Balance>
                  </Td>
                  <Td>
                    <Badge
                      variant={statusVariantMap[txn.status]}
                      size="sm"
                    >
                      {txn.status.toUpperCase()}
                    </Badge>
                  </Td>
                </Tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Card>
  );
};

export default TransactionsTable;
